/**
 * OpportunityDetailDialog — full real-data detail for a persisted Quick Winner
 * opportunity. Every value is read from the canonical opportunity document;
 * profit/ROI/margin render only when economics.complete is persisted.
 * Only "Refresh Walmart Data" spends a provider request (governed budget).
 */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { PriceHistoryChart } from "@/components/product-card/PriceHistoryChart";
import {
  X,
  MoreVertical,
  Flag,
  Folder,
  Bell,
  ChevronLeft,
  ChevronRight,
  Bookmark,
  RefreshCw,
  ExternalLink,
  ScanSearch,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { money } from "@/lib/format";
import { economicsKindLabel } from "@/lib/economicsLabels";
import {
  WalmartAttributesPanel,
  useWalmartAttributes,
} from "@/components/analyzer/WalmartAttributesPanel";
import { RatingStar } from "@/components/common/primitives";
import { walmartSellingStatus, SELLING_STATUS_LABEL } from "@/lib/dealFlow/adapter";
import "@/components/product-card/product-card.css";
import {
  CollectionPicker,
  useLeadCollections,
} from "@/components/leads/CollectionPicker";

/** Above AppShell sidebar (z-30), sidebar flyouts (z-80), and content column. */
const DIALOG_Z = "z-[110]";

function dash(v) {
  if (v === null || v === undefined || v === "") return "—";
  return v;
}

function numOrDash(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n : "—";
}

async function copyText(text) {
  if (!text) return false;
  try {
    await navigator.clipboard.writeText(String(text));
    return true;
  } catch {
    return false;
  }
}

function matchBadgeClass(status) {
  if (status === "verified" || status === "exact") return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (status === "probable") return "text-signal-blue bg-blue-50 border-blue-200";
  if (status === "review") return "text-amber-700 bg-amber-50 border-amber-200";
  if (status === "rejected") return "text-red-600 bg-red-50 border-red-200";
  return "text-ink-muted bg-surface-1 border-edge-subtle";
}

function Section({ title, children, defaultOpen = true, anchor, highlight }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className={cn(
        "border-b border-edge-subtle",
        // Analysis focus highlights the sections it scrolled to, so the user can
        // see WHERE they landed instead of guessing.
        highlight && "ring-2 ring-inset ring-signal-blue/40 bg-signal-blue/[0.04]"
      )}
      data-section={anchor || undefined}
      id={anchor ? `detail-section-${anchor}` : undefined}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 text-left"
      >
        <span className="text-[11px] font-bold uppercase tracking-wider text-ink-muted">{title}</span>
        <span className={cn("text-ink-muted transition-transform", open && "rotate-180")}>⌄</span>
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

function Kv({ rows }) {
  return (
    <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-2 text-[13px]">
      {rows.map(([k, v, cls], i) => (
        <React.Fragment key={i}>
          <dt className="text-ink-muted">{k}</dt>
          <dd className={cn("m-0 font-semibold tabular-nums text-right", cls)}>{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function ComparisonImage({ src, alt }) {
  const ref = React.useRef(null);
  const [active, setActive] = React.useState(false);
  const [pos, setPos] = React.useState({ x: 50, y: 50 });
  const canHover =
    typeof window !== "undefined" && window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;

  if (!src) {
    return (
      <div className="pc-comparison-side-image bg-surface-1">
        <span className="text-[11px] text-ink-muted">No image</span>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className="pc-comparison-side-image"
      data-testid="product-comparison-image"
      onMouseEnter={() => canHover && setActive(true)}
      onMouseLeave={() => setActive(false)}
      onMouseMove={(e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        setPos({
          x: Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100)),
          y: Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100)),
        });
      }}
    >
      <img
        src={src}
        alt={alt || ""}
        loading="lazy"
        style={
          active
            ? { transform: "scale(2.2)", transformOrigin: `${pos.x}% ${pos.y}%` }
            : undefined
        }
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    </div>
  );
}

function ProductSide({ label, image, images, merchant, title, brand, price, currency, availability, upc, gtin, pack, url, checkedAt, accent }) {
  return (
    <div className="rounded-xl border border-edge-subtle overflow-hidden">
      <div className={cn("px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-white", accent)}>{label}</div>
      <div className="p-3.5 space-y-2">
        <ComparisonImage src={image || (Array.isArray(images) && images[0]) || null} alt={title || label} />
        <div>
          <div className="text-[11px] font-semibold text-ink-muted">{merchant || label}</div>
          <div className="text-[13px] font-semibold leading-snug mt-0.5">{dash(title)}</div>
          {brand && <div className="text-[11px] text-ink-muted">Brand: {brand}</div>}
        </div>
        <div className="text-[16px] font-bold tabular-nums">{money(price, currency)}</div>
        <Kv rows={[
          ["Availability", dash(availability)],
          ["UPC", dash(upc)],
          ["GTIN", dash(gtin)],
          ["Pack", numOrDash(pack)],
        ]} />
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12px] font-semibold text-signal-blue hover:underline">
            Open listing <ExternalLink className="w-3 h-3" />
          </a>
        ) : (
          <div className="text-[11px] text-ink-muted">No listing URL</div>
        )}
        {checkedAt && <div className="text-[10.5px] text-ink-muted">Last checked {new Date(checkedAt).toLocaleString()}</div>}
      </div>
    </div>
  );
}

export function OpportunityDetailDialog({
  lead,
  list,
  index,
  onIndex,
  onClose,
  onSave,
  onRefresh,
  history,
  coverageMeta = null,
  historyLoading = false,
  historyError = null,
  onHistoryRetry = null,
  // "analysis" opens the dialog already scrolled to the profitability and match
  // evidence sections. Same dialog, same data — only the initial position
  // differs, so there is no second popup implementation.
  focus = null,
}) {
  // Analysis focus: scroll to the profitability block after mount. Runs once per
  // open; the user can scroll freely afterwards.
  React.useEffect(() => {
    if (focus !== "analysis") return undefined;
    const id = window.requestAnimationFrame(() => {
      const el = document.getElementById("detail-section-deal-math");
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [focus]);

  const [moreOpen, setMoreOpen] = useState(false);
  const [flagged, setFlagged] = useState(false);
  const [tracked, setTracked] = useState(false);
  // Same shared attribute cache as the Product Analyzer details view.
  const { attributes: walmartAttributes, loading: attributesLoading } = useWalmartAttributes(
    lead?.walmartItemId
  );
  // Stage Z PR C: the collection picker is the SAME implementation used by the
  // cards and Saved Leads. The previous block called
  // `/product-sourcing/collections`, which has no backend route at all — it was
  // decorative and always failed.
  const [collectOpen, setCollectOpen] = useState(false);
  const collectionState = useLeadCollections(lead);
  const [savingForCollection, setSavingForCollection] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const selling = walmartSellingStatus(lead.walmartAvailability);

  // Keyboard: Escape closes; arrows navigate. Restore focus on unmount.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && index > 0) onIndex(index - 1);
      if (e.key === "ArrowRight" && index < list.length - 1) onIndex(index + 1);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }, [index, list.length, onClose, onIndex]);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh(lead);
      toast.success("Walmart data refreshed");
    } catch (err) {
      toast.error(err?.message || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const doCopy = async (label, value) => {
    const ok = await copyText(value);
    toast[ok ? "success" : "error"](ok ? `${label} copied` : "Copy failed");
    setMoreOpen(false);
  };

  const toggleFlag = async () => {
    const next = !flagged;
    setFlagged(next);
    try {
      const { api } = await import("@/lib/api");
      if (next) await api.productSourcingFlag(lead.id);
      else await api.productSourcingUnflag(lead.id);
      toast.success(next ? "Flagged" : "Unflagged");
    } catch {
      setFlagged(!next);
      toast.error("Couldn't update flag");
    }
  };

  const toggleTrack = async () => {
    const next = !tracked;
    setTracked(next);
    try {
      const { api } = await import("@/lib/api");
      await api.productSourcingSetTracking(lead.id, next);
      toast.success(next ? "Tracking enabled" : "Tracking disabled");
    } catch {
      setTracked(!next);
      toast.error("Couldn't update tracking");
    }
  };

  /**
   * Open the collection picker. Never closes or resets the product popup — the
   * picker is a nested overlay, so Match Analysis / Price History / navigation
   * all keep their state.
   */
  const openCollect = () => setCollectOpen(true);

  /**
   * Add to a collection, saving the product first when it is not saved yet.
   *
   * Requirement: the save and the membership must produce a CONSISTENT persisted
   * result. We await the real save before touching membership, so a failed save
   * can never leave a membership pointing at a lead that does not exist.
   */
  const handlePickCollection = async (collection, makeMember) => {
    if (makeMember && !lead.saved) {
      if (typeof onSave !== "function") {
        toast.error("Save this product before adding it to a collection.");
        return false;
      }
      setSavingForCollection(true);
      try {
        const result = await onSave(lead);
        if (result && result.ok === false) {
          toast.error(result.error || "Could not save this product.");
          return false;
        }
      } finally {
        setSavingForCollection(false);
      }
    }
    const ok = await collectionState.toggle(collection, makeMember);
    if (ok) {
      // Keep Saved Leads counts/chips in step immediately after the mutation.
      window.dispatchEvent(new CustomEvent("izisell:collections-changed"));
    }
    return ok;
  };

  const handleCreateCollectionFromPopup = async (fields) => {
    const created = await collectionState.createAndAdd(fields);
    if (created) {
      window.dispatchEvent(new CustomEvent("izisell:collections-changed"));
    }
    return created;
  };

  const isReviewLike = ["review", "probable"].includes(lead.matchStatus);

  // Portal to document.body so fixed positioning is viewport-relative and
  // never trapped under the AppShell sidebar stacking context / margin column.
  if (typeof document === "undefined") return null;

  const dialog = (
    <div
      className={cn(
        // Portaled to body: covers full viewport even when the content column
        // has md:ml-[240px] or a squeezed tablet window — never under the sidebar.
        "fixed inset-0 grid place-items-center overflow-y-auto overscroll-contain p-3 sm:p-6 bg-black/60 backdrop-blur-sm",
        DIALOG_Z,
      )}
      data-testid="opportunity-detail-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-[min(1000px,calc(100vw-1.5rem))] max-h-[min(90vh,100dvh-1.5rem)] my-auto flex flex-col">
        <div
          className="bg-white rounded-[18px] overflow-hidden flex flex-col min-h-0 max-h-[inherit] shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-label={lead.walmartTitle || lead.sourceTitle || lead.id}
          data-testid="opportunity-detail-dialog"
        >
          {/* Header */}
          <header className="flex-none flex items-center gap-3 px-5 py-3.5 border-b border-edge-subtle">
            <div className="min-w-0 flex-1">
              <h3 className="m-0 text-[15px] font-bold truncate">{dash(lead.walmartTitle || lead.sourceTitle)}</h3>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded border", matchBadgeClass(lead.matchStatus))}>
                  {lead.matchStatusLabel || "—"}
                </span>
                {lead.matchConfidence != null && (
                  <span className="text-[11px] text-ink-muted">confidence {lead.matchConfidence}%</span>
                )}
                <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded", selling === "ACTIVE" ? "text-emerald-700 bg-emerald-50" : selling === "OUT_OF_STOCK" ? "text-amber-700 bg-amber-50" : "text-ink-muted bg-surface-1")}>
                  {SELLING_STATUS_LABEL[selling] || "—"}
                </span>
                {lead.rating != null && (
                  <span className="inline-flex items-center gap-1 text-[11px]">
                    <RatingStar /> <b>{lead.rating.toFixed(1)}</b>
                    {lead.reviews != null && <span className="text-ink-muted">({Number(lead.reviews).toLocaleString()})</span>}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button type="button" aria-label="Previous" disabled={index <= 0} onClick={() => onIndex(index - 1)}
                className="w-8 h-8 rounded-full border border-edge-subtle grid place-items-center disabled:opacity-35 hover:bg-surface-1">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button type="button" aria-label="Next" disabled={index >= list.length - 1} onClick={() => onIndex(index + 1)}
                className="w-8 h-8 rounded-full border border-edge-subtle grid place-items-center disabled:opacity-35 hover:bg-surface-1">
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className="relative">
                <button type="button" aria-label="More actions" onClick={() => setMoreOpen((v) => !v)}
                  className="w-8 h-8 rounded-full border border-edge-subtle grid place-items-center hover:bg-surface-1">
                  <MoreVertical className="w-4 h-4" />
                </button>
                {moreOpen && (
                  <div className="absolute right-0 top-10 bg-white border border-edge-subtle rounded-xl shadow-xl min-w-[210px] z-20 p-1.5">
                    {[
                      ["Copy Walmart Item ID", lead.walmartItemId],
                      ["Copy UPC", lead.upc],
                      ["Copy GTIN", lead.gtin],
                      ["Copy Walmart URL", lead.walmartUrl],
                      ["Copy Source URL", lead.sourceUrl],
                    ].filter(([, v]) => v).map(([label, value]) => (
                      <button key={label} type="button" onClick={() => doCopy(label, value)}
                        className="w-full text-left px-3 py-2 rounded-lg text-[13px] hover:bg-surface-1">
                        {label}
                      </button>
                    ))}
                    <hr className="my-1 border-edge-subtle" />
                    <button type="button" onClick={toggleFlag} className="w-full text-left px-3 py-2 rounded-lg text-[13px] hover:bg-surface-1">
                      {flagged ? "Unflag" : "Flag"}
                    </button>
                    <button type="button" onClick={openCollect} className="w-full text-left px-3 py-2 rounded-lg text-[13px] hover:bg-surface-1">
                      Add to collection
                    </button>
                    <button type="button" onClick={doRefresh} className="w-full text-left px-3 py-2 rounded-lg text-[13px] hover:bg-surface-1">
                      Refresh Walmart Data
                    </button>
                  </div>
                )}
              </div>
              <button type="button" aria-label="Close" onClick={onClose}
                className="w-8 h-8 rounded-full border border-edge-subtle grid place-items-center hover:bg-surface-1">
                <X className="w-4 h-4" />
              </button>
            </div>
          </header>

          {/* Action bar */}
          <div className="flex-none flex items-center gap-2 px-5 py-2.5 border-b border-edge-subtle flex-wrap">
            <ActionBtn onClick={() => onSave(lead)} active={lead.saved} title={lead.saved ? "Remove from saved" : "Save"} disabled={!lead.walmartItemId}>
              <Bookmark className="w-4 h-4" fill={lead.saved ? "currentColor" : "none"} />
            </ActionBtn>
            <ActionBtn onClick={toggleTrack} active={tracked} title={tracked ? "Stop tracking" : "Track price"}>
              <Bell className="w-4 h-4" fill={tracked ? "currentColor" : "none"} />
            </ActionBtn>
            <ActionBtn onClick={openCollect} title="Add to collection">
              <Folder className="w-4 h-4" />
            </ActionBtn>
            <ActionBtn onClick={toggleFlag} active={flagged} title={flagged ? "Unflag" : "Flag"}>
              <Flag className="w-4 h-4" fill={flagged ? "currentColor" : "none"} />
            </ActionBtn>
            <button type="button" onClick={doRefresh} disabled={refreshing || !lead.walmartItemId}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-edge-subtle bg-white text-[12px] font-semibold hover:bg-surface-1 disabled:opacity-40">
              <RefreshCw className={cn("w-3.5 h-3.5", refreshing && "animate-spin")} /> Refresh Walmart Data
            </button>
            <button type="button"
              onClick={() => { if (lead.walmartItemId) window.open(`/app/analyzer?itemId=${encodeURIComponent(lead.walmartItemId)}`, "_blank"); }}
              disabled={!lead.walmartItemId}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-navy-900 text-white text-[12px] font-semibold hover:bg-navy-800 disabled:opacity-40">
              <ScanSearch className="w-3.5 h-3.5" /> Open in Product Analyzer
            </button>
          </div>

          {/* Scrollable body — header/actions stay pinned */}
          <div className="overflow-y-auto overflow-x-hidden min-h-0 flex-1 overscroll-contain">
            <Section title="Product comparison">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ProductSide
                  label="Source" accent="bg-emerald-600"
                  image={lead.sourceImage} merchant={lead.sourceMerchant} title={lead.sourceTitle}
                  brand={lead.sourceBrand} price={lead.sourcePrice} currency={lead.sourceCurrency}
                  availability={lead.sourceAvailability} upc={lead.upc} gtin={lead.gtin}
                  pack={lead.sourcePackCount} url={lead.sourceUrl} checkedAt={lead.collectedAt}
                />
                <ProductSide
                  label="Walmart" accent="bg-[#0071dc]"
                  image={lead.walmartImage} merchant={lead.walmartSeller} title={lead.walmartTitle}
                  brand={lead.walmartBrand} price={lead.walmartPrice} currency={lead.walmartCurrency}
                  availability={lead.walmartAvailability} upc={lead.upc} gtin={lead.gtin}
                  pack={lead.walmartPackCount} url={lead.walmartUrl} checkedAt={lead.checkedAt}
                />
              </div>
            </Section>

            <Section
              title="Match analysis"
              anchor="match-analysis"
              highlight={focus === "analysis"}
            >
              <Kv rows={[
                ["Status", lead.matchStatusLabel || "—"],
                ["Confidence", lead.matchConfidence != null ? `${lead.matchConfidence}%` : "—"],
                ["Identity tier", dash(lead.matchTier)],
                ["Decision reason", dash(lead.matchReason)],
              ]} />
              {isReviewLike && (
                <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-2">
                  This match needs review: identity evidence is incomplete. It has not been
                  verified as the same pack/variant, so it is not treated as an actionable lead.
                </p>
              )}
              {lead.matchMatchedEvidence?.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10.5px] font-bold uppercase text-ink-muted">Matched evidence</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {lead.matchMatchedEvidence.map((e) => <span key={e} className="text-[11px] bg-emerald-50 text-emerald-700 rounded px-1.5 py-0.5">{e}</span>)}
                  </div>
                </div>
              )}
              {lead.matchMissingEvidence?.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10.5px] font-bold uppercase text-ink-muted">Missing evidence</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {lead.matchMissingEvidence.map((e) => <span key={e} className="text-[11px] bg-amber-50 text-amber-700 rounded px-1.5 py-0.5">{e}</span>)}
                  </div>
                </div>
              )}
            </Section>

            <Section title="Price history">
              {/* Shared chart: renders a real marker from the FIRST observation
                  instead of a text card, and never fabricates points. */}
              <PriceHistoryChart
                rows={history}
                coverageMeta={coverageMeta}
                loading={historyLoading}
                error={historyError}
                onRetry={onHistoryRetry || undefined}
                testId="detail-price-history"
              />
            </Section>

            <Section title="Walmart attributes">
              <WalmartAttributesPanel attributes={walmartAttributes} loading={attributesLoading} compact />
            </Section>

            <Section
              title="Deal math"
              anchor="deal-math"
              highlight={focus === "analysis"}
            >
              <Kv rows={[
                ["Opportunity status", lead.opportunityStatusLabel || "—"],
                ["Source price", money(lead.sourcePrice, lead.sourceCurrency)],
                ["Walmart price", money(lead.walmartPrice, lead.walmartCurrency)],
                ["Gross price difference", lead.spread != null ? money(lead.spread, lead.sourceCurrency) : "—"],
                ["Referral fee", lead.referralFee != null ? money(lead.referralFee, lead.sourceCurrency) : (lead.fees != null && lead.excludesWfs ? money(lead.fees, lead.sourceCurrency) : "—")],
                ["WFS / fulfillment fee", lead.excludesWfs
                  ? "Not included"
                  : (lead.wfsFee != null ? money(lead.wfsFee, lead.sourceCurrency) : (lead.fulfillmentFee != null ? money(lead.fulfillmentFee, lead.sourceCurrency) : "—"))],
                ["Economics kind", economicsKindLabel(lead.economicsKind) || "Not classified"],
                ["Net profit", lead.profit != null ? money(lead.profit, lead.sourceCurrency) : "—"],
                ["ROI", lead.roi != null ? `${Number(lead.roi).toFixed(1)}%` : "—"],
                ["Margin", lead.margin != null ? `${Number(lead.margin).toFixed(1)}%` : "—"],
                ["ROI denominator", lead.roiDenominator || "effective_acquisition_cost"],
              ]} />
              {(lead.opportunityStatus === "NEEDS_REVIEW" || lead.opportunityStatus === "ESTIMATED")
                && (lead.missingRequirements?.length > 0 || lead.reviewReasonLabels?.length > 0) && (
                <p
                  className={
                    lead.opportunityStatus === "ESTIMATED"
                      ? "text-[11.5px] text-blue-800 mt-2 m-0"
                      : "text-[11.5px] text-amber-800 mt-2 m-0"
                  }
                  data-testid="deal-flow-detail-review-reasons"
                >
                  {lead.opportunityStatus === "ESTIMATED" ? "Outstanding verification: " : "Needs review: "}
                  {(lead.missingRequirements?.length ? lead.missingRequirements : lead.reviewReasonLabels).join(" · ")}
                  {lead.checkedAt ? ` · Last checked ${new Date(lead.checkedAt).toLocaleString()}` : ""}
                </p>
              )}
              {!lead.economicsComplete && (
                <p className="text-[11.5px] text-ink-muted mt-2 m-0">
                  Profitability requires complete cost inputs. Missing inputs stay "—" —
                  price difference alone is not net profit.
                </p>
              )}
              {lead.economicsComplete && lead.excludesWfs && (
                <p className="text-[11.5px] text-ink-muted mt-2 m-0">
                  Fee-excluded estimate: WFS and storage fees are not included (not invented as zero).
                </p>
              )}
            </Section>

            <Section title="Estimate calculator" defaultOpen={false}>
              <p className="text-[11.5px] text-ink-muted m-0">
                Enter your own assumptions to estimate economics. These are user assumptions,
                clearly labeled, and never replace observed data.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                {["Prep", "Inbound shipping", "Packaging", "Other cost"].map((label) => (
                  <label key={label} className="text-[11px] font-semibold text-ink-muted">
                    {label}
                    <input type="number" min="0" step="0.01" placeholder="0.00"
                      className="w-full h-9 mt-1 px-2.5 rounded-lg border border-edge-subtle text-compact" />
                  </label>
                ))}
              </div>
              <p className="text-[10.5px] text-amber-700 mt-2 m-0">ESTIMATE — not observed economics.</p>
            </Section>

            <Section title="Product details" defaultOpen={false}>
              <Kv rows={[
                ["Brand", dash(lead.walmartBrand || lead.sourceBrand)],
                ["Manufacturer", dash(lead.walmartManufacturer)],
                ["Category", dash(lead.category)],
                ["Model", dash(lead.walmartModel || lead.sourceModel)],
                ["MPN", dash(lead.walmartMpn || lead.sourceMpn)],
                ["Size", dash(lead.walmartSize || lead.sourceSize)],
                ["Color", dash(lead.walmartColor || lead.sourceColor)],
                ["Pack", numOrDash(lead.walmartPackCount || lead.sourcePackCount)],
                ["Condition", dash(lead.walmartCondition || lead.sourceCondition)],
              ]} />
              {lead.walmartDescription && (
                <div className="mt-3">
                  <div className="text-[10.5px] font-bold uppercase text-ink-muted">Description</div>
                  <p className="text-[13px] text-ink-secondary leading-relaxed mt-1 m-0">{lead.walmartDescription}</p>
                </div>
              )}
              {lead.walmartFeatures?.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10.5px] font-bold uppercase text-ink-muted">Key features</div>
                  <ul className="m-0 mt-1 pl-4 text-[13px] text-ink-secondary space-y-0.5">
                    {lead.walmartFeatures.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                </div>
              )}
            </Section>

            <Section title="Inventory & availability" defaultOpen={false}>
              <Kv rows={[
                ["Availability", dash(lead.walmartAvailability)],
                ["Seller", dash(lead.walmartSeller)],
                ["Sellers", numOrDash(lead.walmartSellerCount)],
                ["Delivery", dash(lead.walmartDeliveryText)],
                ["Pickup", dash(lead.walmartPickupText)],
                ["Shipping", dash(lead.walmartShippingText)],
                ["Sold & shipped by", dash(lead.walmartSoldAndShippedBy)],
              ]} />
              {lead.walmartSellerCount == null ? (
                <p className="text-[11.5px] text-ink-muted mt-2 m-0">
                  Seller count not captured yet.
                </p>
              ) : null}
            </Section>

            <Section title="Ratings & reviews" defaultOpen={false}>
              <Kv rows={[
                ["Rating", lead.rating != null ? `${lead.rating.toFixed(1)} ★` : "—"],
                ["Reviews", lead.reviews != null ? Number(lead.reviews).toLocaleString() : "—"],
              ]} />
            </Section>

            <Section title="Offers" defaultOpen={false}>
              <p className="text-[13px] text-ink-muted m-0">
                No marketplace offers captured yet.
              </p>
            </Section>
          </div>
        </div>
      </div>

      {/* Collection picker — nested overlay inside the portaled root */}
      {collectOpen && (
        <div
          className="absolute inset-0 z-[1] grid place-items-center p-6 bg-black/40"
          onClick={(e) => { if (e.target === e.currentTarget) setCollectOpen(false); }}
        >
          <div className="w-[min(360px,100%)] bg-white rounded-xl shadow-2xl overflow-hidden">
            <header className="flex items-center px-4 py-3 border-b border-edge-subtle">
              <h4 className="m-0 text-sm font-bold flex-1">Add to collection</h4>
              <button type="button" onClick={() => setCollectOpen(false)} className="w-7 h-7 rounded-full border border-edge-subtle grid place-items-center"><X className="w-3.5 h-3.5" /></button>
            </header>
            <div className="p-3 max-h-[60vh] overflow-y-auto">
              {!lead.saved ? (
                <p
                  className="mb-2 text-[12px] text-ink-muted"
                  data-testid="popup-collection-save-first"
                >
                  This product is not saved yet. Adding it to a collection will save it
                  first so the membership stays durable.
                </p>
              ) : null}
              <CollectionPicker
                lead={lead}
                collections={collectionState.collections}
                memberIds={collectionState.memberIds}
                busyIds={collectionState.busyIds}
                creating={collectionState.creating || savingForCollection}
                onToggle={handlePickCollection}
                onCreate={handleCreateCollectionFromPopup}
                onDone={() => setCollectOpen(false)}
                onRefresh={collectionState.refresh}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(dialog, document.body);
}

function ActionBtn({ children, onClick, title, active, disabled }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "w-8 h-8 rounded-lg border border-edge-subtle bg-white grid place-items-center",
        disabled && "opacity-40 cursor-not-allowed",
        active ? "text-emerald-700 border-emerald-300 bg-emerald-50" : "hover:bg-surface-1",
      )}
    >
      {children}
    </button>
  );
}

export default OpportunityDetailDialog;
